import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, ObjectStatus, Button, Switch, Label, SegmentedButton, SegmentedButtonItem, MessageStrip, BusyIndicator, FlexBox, Input } from '@ui5/webcomponents-react';
import { api, ApiError, type Rec } from '../api/client';
import { useAuth } from '../store/auth';
import { ListPage } from '../components/ListPage';
import { ScanButton } from '../components/BarcodeScanner';
import { useIsMobile } from '../hooks/useIsMobile';
import { findField, formatValue, interpolate, templateFields, labelFor, recordKey } from '../render/format';
import { csvFileName, downloadCsv, toCsv } from '../render/csv';
import { detectChannel } from '../platform';

/** Generic Fiori list report rendered from a ScreenDef stored in SAP. Route: /s/:screenId */
export function ListScreen() {
  const { screenId = '' } = useParams();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const { principal, can } = useAuth();
  const [search, setSearch] = useState('');
  const [quick, setQuick] = useState<number>(0);
  const [mine, setMine] = useState(false);
  const [keyDraft, setKeyDraft] = useState('');
  const [scanned, setScanned] = useState<string | null>(null);   // set by a scan; cleared once the results land
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);

  const def = useQuery({ queryKey: ['screen', screenId], queryFn: () => api.screen(screenId) });
  const meta = useQuery({ queryKey: ['meta', def.data?.entity], queryFn: () => api.entityMeta(def.data!.entity), enabled: !!def.data, staleTime: Infinity });
  const d = def.data;
  const page = d?.list.pageSize ?? 50;
  const filters = [d?.list.filter, d?.list.quickFilters?.[quick - 1]?.filter, mine ? `U_AppUser eq '${principal?.user}'` : undefined].filter(Boolean) as string[];
  const keyField = d?.entity ? meta.data?.keys[0] : undefined;

  const q = useInfiniteQuery({
    queryKey: ['list', screenId, search, filters, page],
    enabled: !!d && !!meta.data,
    queryFn: ({ pageParam }) => api.list(d!.entity, {
      q: search || undefined, filter: filters.join(' and ') || undefined, orderby: d!.list.orderby,
      select: [...new Set([...(meta.data?.keys ?? []), ...d!.list.columns.flatMap(c => c.template ? templateFields(c.template) : [c.field]), 'DocCurrency', 'Currency'].filter(f => !meta.data || findField(meta.data, f)))].join(','),
      top: page, skip: pageParam, count: filters.length > 0, searchFields: d!.list.searchFields?.join(','),
    }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.value.length < page ? undefined : all.length * page),
  });
  const rows = q.data?.pages.flatMap(p => p.value) ?? [];
  const count = q.data?.pages[0]?.['@odata.count'];

  // A scan that matches exactly one record opens it straight away — the point of scanning in the field.
  // Declared before any early return so the hook order never changes.
  useEffect(() => {
    if (!scanned || q.isFetching) return;
    setScanned(null);
    if (rows.length === 1) { const k = recordKey(meta.data, rows[0]); if (k) nav(`/s/${screenId}/${encodeURIComponent(k)}`); }
  }, [scanned, q.isFetching, rows, meta.data, screenId, nav]);

  if (def.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;
  if (def.error || !d) return <MessageStrip design="Negative" hideCloseButton>{(def.error as Error)?.message ?? 'Screen not found'}</MessageStrip>;

  // A screen may stand in for a custom page (a report, a wizard) that the generic renderer cannot express.
  // It still lives in @B1LSCREEN, so it is menued, pack-gated and access-controlled like any other screen.
  if (d.openRoute) return <Navigate to={d.openRoute} replace />;

  // Some entities can't be listed by SAP (images: "query is not supported") or are refused because the query
  // crashes Service Layer — they can still be opened by key, so offer that instead of a dead error.
  const unlistable = q.error instanceof ApiError && (q.error.sapCode === '220' || q.error.sapCode === 'B1L-UNSAFE-LIST');
  const keyNames = meta.data?.keys ?? [];
  const openByKey = () => { const v = keyDraft.trim(); if (v) nav(`/s/${screenId}/${encodeURIComponent(v)}`); };
  const open = (r: Rec) => { const k = recordKey(meta.data, r); if (k) nav(`/s/${screenId}/${encodeURIComponent(k)}`); };

  /** Export what the list currently shows — same filters, same columns, every matching row (not just the loaded page). */
  const exportCsv = async () => {
    if (!d || !meta.data) return;
    setExporting(true); setExportNote(null);
    try {
      const res = await api.exportRows(d.entity, {
        q: search || undefined, filter: filters.join(' and ') || undefined, orderby: d.list.orderby,
        select: [...new Set([...(meta.data.keys ?? []), ...d.list.columns.flatMap(c => c.template ? templateFields(c.template) : [c.field]), 'DocCurrency', 'Currency'].filter(f => findField(meta.data, f)))].join(','),
        searchFields: d.list.searchFields?.join(','),
      });
      const csv = toCsv(res.value as Rec[], d.list.columns, meta.data, currencyOf);
      if (!downloadCsv(csvFileName(d.title), csv)) { setExportNote('This device cannot save files — open B1Links in a browser to export.'); return; }
      setExportNote(res.truncated ? `Exported the first ${res.value.length} rows (limit ${res.max}). Narrow the search or filters for the rest.` : `Exported ${res.value.length} row(s).`);
    } catch (e) { setExportNote((e as Error).message); } finally { setExporting(false); }
  };
  const currencyOf = (r: Rec) => (r.DocCurrency ?? r.Currency) as string | undefined;

  // One value renderer shared by the desktop table cells and the mobile cards.
  type Col = (typeof d.list.columns)[number];
  const cell = (c: Col, r: Rec) => c.template
    ? <span>{interpolate(c.template, r)}</span>
    : c.format === 'status'
    ? <ObjectStatus state={c.statusMap?.[String(r[c.field])] ?? 'None'}>{formatValue(r[c.field], c, findField(meta.data, c.field))}</ObjectStatus>
    : <span>{formatValue(r[c.field], c, findField(meta.data, c.field), c.format === 'money' ? currencyOf(r) : undefined)}</span>;

  return (
    <ListPage title={d.title} count={count} loading={q.isFetching} error={unlistable ? null : q.error as Error | null} search={search} onSearch={setSearch}
      hasMore={q.hasNextPage} onLoadMore={() => q.fetchNextPage()}
      scan={<ScanButton tooltip="Scan a barcode to search" onCode={code => { setSearch(code); setScanned(code); }} />}
      actions={<>
        {d.list.quickFilters?.length ? (
          <SegmentedButton onSelectionChange={e => setQuick(Number((e.detail.selectedItems[0] as HTMLElement).dataset.i))}>
            <SegmentedButtonItem data-i={0} selected={quick === 0}>All</SegmentedButtonItem>
            {d.list.quickFilters.map((f, i) => <SegmentedButtonItem key={f.label} data-i={i + 1} selected={quick === i + 1}>{f.label}</SegmentedButtonItem>)}
          </SegmentedButton>) : <></>}
        {d.list.mineFilter ? <><Label>Mine</Label><Switch checked={mine} onChange={() => setMine(m => !m)} /></> : <></>}
        {/* Android's WebView blocks file downloads, so the button is hidden there rather than failing silently. */}
        {detectChannel() !== 'mobile' ? <Button icon="excel-attachment" disabled={exporting || !meta.data} onClick={exportCsv}>{exporting ? 'Exporting…' : 'Export'}</Button> : <></>}
        {d.form.canCreate && (d.access ? d.access === 'edit' : can(`${d.entity}.Edit`)) ? <Button design="Emphasized" icon="add" onClick={() => nav(d.form.createRoute ?? `/s/${screenId}/new`)}>New</Button> : <></>}
      </>}>
      {exportNote && <MessageStrip design="Information" onClose={() => setExportNote(null)} style={{ marginBottom: '0.5rem' }}>{exportNote}</MessageStrip>}
      {unlistable && (
        <FlexBox direction="Column" style={{ gap: '0.5rem', maxWidth: '36rem' }}>
          <MessageStrip design="Information" hideCloseButton>{q.error!.message} Enter the {keyNames.length > 1 ? `key as ${keyNames.map(k => `${k}=value`).join(',')}` : keyNames[0] ?? 'key'} to open a record.</MessageStrip>
          <FlexBox style={{ gap: '0.5rem' }}>
            <Input value={keyDraft} placeholder={keyNames.join(', ')} style={{ flex: 1 }} onInput={e => setKeyDraft(e.target.value ?? '')} onChange={openByKey} />
            <Button design="Emphasized" disabled={!keyDraft.trim()} onClick={openByKey}>Open</Button>
          </FlexBox>
        </FlexBox>
      )}
      {isMobile ? (
        // Phone: one card per record — primary field as the heading, the rest as label/value rows.
        <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
          {rows.map((r, i) => {
            const [first, ...rest] = d.list.columns;
            return (
              <div key={recordKey(meta.data, r) ?? i} onClick={() => open(r)}
                style={{ background: 'var(--sapTile_Background, #fff)', borderRadius: '0.5rem', padding: '0.7rem 0.85rem', cursor: keyField ? 'pointer' : 'default', boxShadow: 'var(--sapContent_Shadow0, 0 0 0 1px rgba(0,0,0,0.08))' }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: rest.length ? '0.4rem' : 0 }}>{cell(first, r)}</div>
                {rest.map(c => (
                  <FlexBox key={c.field} justifyContent="SpaceBetween" alignItems="Center" style={{ gap: '0.75rem', padding: '0.12rem 0', fontSize: '0.85rem' }}>
                    <span style={{ color: 'var(--sapNeutralTextColor)', whiteSpace: 'nowrap' }}>{labelFor(c.field, findField(meta.data, c.field), c.label)}</span>
                    <span style={{ textAlign: 'right', minWidth: 0 }}>{cell(c, r)}</span>
                  </FlexBox>
                ))}
              </div>
            );
          })}
        </FlexBox>
      ) : (
        <Table headerRow={<TableHeaderRow sticky>
          {d.list.columns.map(c => <TableHeaderCell key={c.field} width={c.width}><span>{labelFor(c.field, findField(meta.data, c.field), c.label)}</span></TableHeaderCell>)}
        </TableHeaderRow>}>
          {rows.map((r, i) => (
            <TableRow key={recordKey(meta.data, r) ?? i} interactive onClick={() => open(r)}>
              {d.list.columns.map(c => <TableCell key={c.field}>{cell(c, r)}</TableCell>)}
            </TableRow>
          ))}
        </Table>
      )}
    </ListPage>
  );
}
